import React, { useEffect, useRef, useState } from "react";
import { apiFetch, useApp } from "../../context/AppContext";
import { Button, FormErrorsSummary, Modal } from "./UI";
import {
  CheckCircle2, ChevronRight, Download, Upload,
} from "lucide-react";
import { FORM_SECTIONS as DEFAULT_SECTIONS } from "../../data/formQuestionSections";

const TPRM_COLUMNS = [
  ["question", "Question text (required) — same as Question Text in the form"],
  ["category", "Category — use a name from the list below (e.g. Compliance & Regulatory)"],
  ["type", "Field type — boolean, text, select, multiselect, file, etc."],
  ["required", "true or false"],
  ["weight", "Question weight (1–5)"],
  ["options", "For select types: Label:Weight|Label:Weight  e.g. Yes:5|No:1"],
  ["allow_attachment", "true or false"],
  ["attachment_required", "true or false (only when allow_attachment is true)"],
  ["is_active", "true or false"],
  ["order", "Sort order number"],
];

const FORM_COLUMNS = [
  ["section", "Section — use the section name from the list below (e.g. General Info)"],
  ["question", "Question text (required) — same as Question Text in the form"],
  ["description", "Optional help text under the question"],
  ["field_type", "Field type — text, textarea, select, boolean, gap_choice, etc."],
  ["required", "true or false"],
  ["options", "Pipe-separated labels, or Label:Weight for scored options"],
  ["weight", "Question weight (1–5)"],
  ["allow_attachment", "true or false"],
  ["attachment_required", "true or false (only when allow_attachment is true)"],
  ["is_active", "true or false"],
  ["order", "Sort order number"],
];

const GAP_EXTRA_COLUMNS = [
  ["dpdp_ref", "DPDP section reference"],
  ["severity", "High / Medium / Low"],
];

const FIELD_TYPE_ALIASES = {
  "short text": "text",
  "long text": "textarea",
  "single choice": "select",
  "multi choice": "multiselect",
  "single choice (checkbox)": "checkbox_single",
  "multi choice (checkbox)": "checkbox_multi",
  "yes / no": "boolean",
  "yes/no": "boolean",
  "toggle": "toggle",
  "date": "date",
  "rating": "rating",
  "file upload": "file",
  "file": "file",
  "gap choice": "gap_choice",
  "gap choice (y/p/n/n/a)": "gap_choice",
  "yes / partial / no / n/a": "gap_choice",
};

function normalizeLookup(value) {
  return String(value || "").trim().toLowerCase();
}

/** Map CSV section/category cell to internal id — accepts label or id from Add Question dropdown. */
function resolveSectionId(raw, sections, fallback = "general") {
  const value = String(raw || "").trim();
  if (!value) return sections?.[0]?.id || fallback;
  if (!sections?.length) return value;

  const lower = normalizeLookup(value);
  const exactId = sections.find((s) => normalizeLookup(s.id) === lower);
  if (exactId) return exactId.id;

  const exactLabel = sections.find((s) => normalizeLookup(s.label) === lower);
  if (exactLabel) return exactLabel.id;

  const looseLabel = sections.find(
    (s) =>
      s.label.toLowerCase().includes(lower) ||
      lower.includes(s.label.toLowerCase()),
  );
  if (looseLabel) return looseLabel.id;

  return value;
}

function sectionLabel(sectionId, sections) {
  return sections.find((s) => s.id === sectionId)?.label || sectionId;
}

function resolveFieldType(raw, formKey) {
  const value = String(raw || "").trim();
  if (!value) return formKey === "gap_assessment" ? "gap_choice" : "text";
  const alias = FIELD_TYPE_ALIASES[normalizeLookup(value)];
  if (alias) return alias;
  return value;
}

function parseCsv(text) {
  const lines = text.split("\n").filter((l) => l.trim());
  if (lines.length < 2) return [];
  const headers = lines[0]
    .split(",")
    .map((h) => h.trim().toLowerCase().replace(/"/g, ""));
  return lines.slice(1).map((line, i) => {
    const vals = line.split(",").map((v) => v.trim().replace(/^"|"$/g, ""));
    const obj = { _row: i + 2 };
    headers.forEach((h, j) => {
      obj[h] = vals[j] || "";
    });
    return obj;
  });
}

function parseOptions(raw) {
  if (!raw) return [];
  return raw.split("|").map((o) => {
    const [label, w] = o.trim().split(":");
    return w !== undefined
      ? { label: label.trim(), weight: Number(w) || 1 }
      : { label: label.trim(), weight: 1 };
  }).filter((o) => o.label);
}

function parseTprmRows(rows, sections) {
  return rows
    .map((obj, i) => ({
      text: obj.question || obj.text || "",
      category: resolveSectionId(obj.category, sections, "general"),
      type: resolveFieldType(obj.type || obj.field_type, "tprm"),
      required: obj.required !== "false",
      weight: parseFloat(obj.weight) || 1,
      options: parseOptions(obj.options),
      is_active: obj.is_active !== "false",
      allow_attachment: obj.allow_attachment === "true",
      attachment_required: obj.allow_attachment === "true" && obj.attachment_required === "true",
      order: parseInt(obj.order, 10) || i + 1,
    }))
    .filter((q) => q.text.trim());
}

function parseFormRows(rows, formKey, sections) {
  const defaultSection = sections?.[0]?.id || "general";
  return rows
    .map((obj, i) => {
      const text = obj.question || obj.text || "";
      const sectionRaw = obj.section || obj.section_id || "";
      const externalId = obj.question_id || obj.external_id || obj.id || "";
      const payload = {
        section_id: resolveSectionId(sectionRaw, sections, defaultSection),
        external_id: externalId || `bulk_${i + 1}`,
        text,
        description: obj.description || "",
        field_type: resolveFieldType(obj.field_type || obj.type, formKey),
        required: obj.required !== "false",
        options: obj.options ? parseOptions(obj.options) : [],
        config: {},
        weight: parseFloat(obj.weight) || 1,
        allow_attachment: obj.allow_attachment === "true",
        attachment_required: obj.allow_attachment === "true" && obj.attachment_required === "true",
        is_active: obj.is_active !== "false",
        order: parseInt(obj.order, 10) || i + 1,
      };
      if (formKey === "gap_assessment") {
        if (obj.dpdp_ref) payload.config.dpdp_ref = obj.dpdp_ref;
        if (obj.severity) payload.config.severity = obj.severity;
        if (!obj.options) {
          payload.options = parseOptions("Yes:1|Partially:0.5|No:0|N/A:0");
        }
      }
      return payload;
    })
    .filter((q) => q.text.trim());
}

function templateCsv(formKey, sections = []) {
  const label = (id) => sectionLabel(id, sections) || id;

  if (formKey === "tprm") {
    return [
      "question,category,type,required,weight,options,allow_attachment,attachment_required,is_active,order",
      `"Does the vendor have ISO 27001?",${label("compliance")},boolean,true,2,,false,false,true,1`,
      `"What is your annual revenue?",${label("financial")},select,true,1.5,"< $1M:1|$1M-$10M:3|> $10M:5",false,false,true,2`,
    ].join("\n");
  }
  if (formKey === "gap_assessment") {
    return [
      "section,question,field_type,required,options,dpdp_ref,severity,weight,allow_attachment,attachment_required,is_active,order",
      `"${label("consent")}","Is valid consent obtained?",gap_choice,true,"Yes:1|Partially:0.5|No:0|N/A:0",Section 8,High,1,false,false,true,1`,
      `"${label("accountability")}","Is a DPO appointed?",gap_choice,true,"Yes:1|Partially:0.5|No:0|N/A:0",Section 10,Medium,2,false,false,true,2`,
    ].join("\n");
  }
  if (formKey === "ropa") {
    return [
      "section,question,description,field_type,required,options,weight,allow_attachment,attachment_required,is_active,order",
      `"${label("org")}","Legal name of organization",,text,true,,1,false,false,true,1`,
      `"${label("activity")}","Describe the processing activity","Include purpose and data categories",textarea,true,,1,true,false,true,2`,
      `"${label("legal")}","Lawful basis for processing",,select,true,"Consent|Contract|Legal obligation",1,false,false,true,3`,
    ].join("\n");
  }
  if (formKey === "pia") {
    return [
      "section,question,description,field_type,required,options,weight,allow_attachment,attachment_required,is_active,order",
      `"${label("general")}","Project / initiative name",,text,true,,1,false,false,true,1`,
      `"${label("dataDetails")}","What personal data is processed?",,textarea,true,,1,false,false,true,2`,
      `"${label("risk")}","Overall risk level",,select,true,"Low|Medium|High|Critical",2,false,false,true,3`,
    ].join("\n");
  }
  return [
    "section,question,description,field_type,required,options,weight,allow_attachment,attachment_required,is_active,order",
    '"General Info","Describe the processing activity",,textarea,true,,1,false,false,true,1',
    '"General Info","Lawful basis for processing",,select,true,"Consent|Contract|Legal obligation",1,false,false,true,2',
  ].join("\n");
}

function templateFilename(formKey) {
  const names = {
    tprm: "tprm_questions_template.csv",
    gap_assessment: "gap_assessment_questions_template.csv",
    ropa: "ropa_questions_template.csv",
    pia: "pia_questions_template.csv",
  };
  return names[formKey] || "form_questions_template.csv";
}

function previewLabel(item, formKey, sections) {
  if (formKey === "tprm") {
    return (
      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
        <span className="text-[10px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded-full">
          {sectionLabel(item.category, sections)}
        </span>
        <span className="text-[10px] text-slate-400">{item.type}</span>
        <span className="text-[10px] text-slate-400">weight {item.weight}</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
      <span className="text-[10px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded-full">
        {sectionLabel(item.section_id, sections)}
      </span>
      <span className="text-[10px] text-slate-400">{item.field_type}</span>
      <span className="text-[10px] text-slate-400">weight {item.weight}</span>
    </div>
  );
}

function expectedColumns(formKey) {
  if (formKey === "tprm") return TPRM_COLUMNS;
  if (formKey === "gap_assessment") {
    const gapCols = FORM_COLUMNS.filter(([col]) => col !== "description");
    const insertAt = gapCols.findIndex(([col]) => col === "options") + 1;
    return [
      ...gapCols.slice(0, insertAt),
      ...GAP_EXTRA_COLUMNS,
      ...gapCols.slice(insertAt),
    ];
  }
  return FORM_COLUMNS;
}

export default function FormQuestionsBulkUploadModal({
  open,
  onClose,
  formKey,
  sections: sectionsProp,
  onSuccess,
}) {
  const { showNotification } = useApp();
  const fileRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState([]);
  const [fileName, setFileName] = useState("");
  const [parseError, setParseError] = useState("");
  const [uploading, setUploading] = useState(false);

  const isTprm = formKey === "tprm";
  const sections = sectionsProp?.length ? sectionsProp : (DEFAULT_SECTIONS[formKey] || []);
  const columns = expectedColumns(formKey);
  const sectionColumnLabel = isTprm ? "category" : "section";

  useEffect(() => {
    if (!open) {
      setPreview([]);
      setFileName("");
      setParseError("");
    }
  }, [open]);

  const handleFile = (file) => {
    if (!file) return;
    if (!file.name.endsWith(".csv")) {
      setParseError("Please upload a .csv file");
      return;
    }
    setFileName(file.name);
    setParseError("");
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const rows = parseCsv(e.target.result);
        const parsed = isTprm
          ? parseTprmRows(rows, sections)
          : parseFormRows(rows, formKey, sections);
        if (!parsed.length) {
          setParseError("No valid questions found. Check CSV format and required columns.");
          return;
        }
        setPreview(parsed);
      } catch {
        setParseError("Failed to parse CSV. Check the format and try again.");
      }
    };
    reader.readAsText(file);
  };

  const downloadTemplate = () => {
    const csv = templateCsv(formKey, sections);
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = templateFilename(formKey);
    a.click();
  };

  const handleUpload = async () => {
    if (!preview.length) return;
    setUploading(true);
    try {
      const endpoint = isTprm
        ? "/tenant/questionnaire/bulk"
        : `/tenant/form-questions/${formKey}/bulk`;
      const res = await apiFetch(endpoint, {
        method: "POST",
        body: JSON.stringify(preview),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Bulk upload failed");
      showNotification(`Imported ${data.imported ?? preview.length} questions`, "success");
      onSuccess?.();
      onClose();
    } catch (e) {
      showNotification(e.message || "Bulk upload failed", "error");
    } finally {
      setUploading(false);
    }
  };

  const formLabel = {
    tprm: "TPRM",
    gap_assessment: "Gap Assessment",
    ropa: "ROPA",
    pia: "PIA",
  }[formKey] || formKey;

  return (
    <Modal open={open} onClose={onClose} title={`Bulk Upload — ${formLabel}`} size="lg">
      <div className="space-y-4">
        <FormErrorsSummary
          errors={parseError ? { csv: parseError } : {}}
          title="Upload error"
        />

        <button
          type="button"
          onClick={downloadTemplate}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-slate-200 hover:border-blue-300 hover:bg-blue-50 text-slate-600 hover:text-blue-600 transition-all group"
        >
          <div className="p-2 rounded-lg bg-slate-100 group-hover:bg-blue-100 transition-all">
            <Download size={14} className="text-slate-500 group-hover:text-blue-500" />
          </div>
          <div className="text-left">
            <p className="text-sm font-semibold">Download CSV Template</p>
            <p className="text-xs text-slate-400">
              Column names match the Add Question form — {templateFilename(formKey)}
            </p>
          </div>
          <ChevronRight size={14} className="ml-auto text-slate-300 group-hover:text-blue-400" />
        </button>

        {sections.length > 0 && (
          <div className="p-4 rounded-xl bg-indigo-50/60 border border-indigo-100">
            <p className="text-xs font-semibold text-indigo-700 uppercase tracking-wide mb-2">
              Valid {isTprm ? "categories" : "sections"} for {formLabel}
            </p>
            <p className="text-[11px] text-indigo-600/80 mb-2.5">
              Use these exact names in the <code className="font-bold">{sectionColumnLabel}</code> column
              (same as the Section dropdown when adding a question manually).
              Legacy CSVs with internal keys like <code>general</code> or <code>dataDetails</code> still work.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {sections.map((s) => (
                <span
                  key={s.id}
                  className="text-[10px] font-medium text-indigo-800 bg-white border border-indigo-100 px-2 py-1 rounded-lg"
                  title={`Internal key: ${s.id}`}
                >
                  {s.label}
                </span>
              ))}
            </div>
          </div>
        )}

        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            handleFile(e.dataTransfer.files[0]);
          }}
          onClick={() => fileRef.current?.click()}
          className={`cursor-pointer rounded-xl border-2 border-dashed flex flex-col items-center justify-center py-8 gap-2.5 transition-all ${
            dragOver ? "border-blue-400 bg-blue-50" : "border-slate-200 hover:border-slate-400"
          }`}
        >
          <div className={`p-3 rounded-xl ${dragOver ? "bg-blue-100" : "bg-slate-100"}`}>
            <Upload size={20} className={dragOver ? "text-blue-500" : "text-slate-400"} />
          </div>
          <div className="text-center">
            <p className="text-sm font-semibold text-slate-700">{fileName || "Drop your CSV here"}</p>
            <p className="text-xs text-slate-400 mt-0.5">
              {fileName
                ? `${preview.length} question${preview.length !== 1 ? "s" : ""} parsed`
                : "or click to browse files"}
            </p>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={(e) => handleFile(e.target.files[0])}
          />
        </div>

        <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2.5">
            Expected CSV columns
          </p>
          <div className="grid grid-cols-2 gap-y-1.5 gap-x-3">
            {columns.map(([col, desc]) => (
              <div key={col} className="flex items-start gap-1.5">
                <code className="text-[10px] font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded flex-shrink-0">
                  {col}
                </code>
                <span className="text-[11px] text-slate-500 mt-0.5">{desc}</span>
              </div>
            ))}
          </div>
        </div>

        {preview.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Preview</p>
              <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-50 text-blue-600 text-xs font-semibold">
                <CheckCircle2 size={11} />
                {preview.length} ready
              </span>
            </div>
            <div className="space-y-2 max-h-44 overflow-y-auto pr-1">
              {preview.map((q, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-xl border border-slate-100 bg-white">
                  <span className="text-xs font-mono text-slate-300 flex-shrink-0 pt-0.5">#{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-800 font-semibold truncate">{q.text}</p>
                    {previewLabel(q, formKey, sections)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-3 mt-6">
        <Button variant="secondary" onClick={onClose} className="flex-1">Cancel</Button>
        <Button
          onClick={handleUpload}
          loading={uploading}
          disabled={!preview.length}
          leftIcon={<Upload size={14} />}
          className="flex-1"
        >
          Import {preview.length > 0 ? `${preview.length} Questions` : "Questions"}
        </Button>
      </div>
    </Modal>
  );
}
